import { STOpenFolderState } from '@st/sdk'
import { isNotEmpty } from '@st/util/json-value'
import { Option, Schema } from 'effect'

export const LOCAL_GRANTS_COOKIE_NAME = 'st-local-grants'

export type GrantsCookie = {
  folders: Record<string, true>
}

const GrantsCookieSchema = Schema.Struct({
  folders: Schema.Record(Schema.String, Schema.Literal(true))
})
const decodeGrantsCookie = Schema.decodeUnknownOption(Schema.fromJsonString(GrantsCookieSchema))

export function getClientMfaIdentifiers(folderState: STOpenFolderState): string[] {
  return [
    folderState.folder.entities.taxpayer?.ssn,
    folderState.folder.entities.spouse?.ssn,
    folderState.folder.entities.corporation?.ein
  ].filter((identifier): identifier is string => isNotEmpty(identifier) && identifier.length >= 4)
}

export function matchesClientMfaLast4(identifiers: string[], last4: string): boolean {
  return identifiers.some((id) => id.endsWith(last4))
}

export function readGrantsFromCookieHeader(
  cookieHeader: string,
  cookieName: string = LOCAL_GRANTS_COOKIE_NAME
): GrantsCookie {
  let raw: string | undefined
  const prefix = `${cookieName}=`
  for (const part of cookieHeader.split(';')) {
    const trimmed = part.trim()
    if (trimmed.startsWith(prefix)) {
      raw = new URLSearchParams(`value=${trimmed.slice(prefix.length)}`).get('value') ?? undefined
      break
    }
  }

  if (!raw) {
    return { folders: {} }
  }

  return Option.getOrElse(decodeGrantsCookie(raw), () => ({ folders: {} }))
}

export function hasFolderGrantInCookie(
  cookieHeader: string,
  folderId: string,
  cookieName: string = LOCAL_GRANTS_COOKIE_NAME
): boolean {
  const cookie = readGrantsFromCookieHeader(cookieHeader, cookieName)
  return cookie.folders[folderId] === true
}

export function withFolderGrant(
  cookieHeader: string,
  folderId: string,
  cookieName: string = LOCAL_GRANTS_COOKIE_NAME
): GrantsCookie {
  const cookie = readGrantsFromCookieHeader(cookieHeader, cookieName)
  return {
    folders: { ...cookie.folders, [folderId]: true }
  }
}

export function buildGrantCookieAssignment(
  cookieHeader: string,
  folderId: string,
  cookieName: string = LOCAL_GRANTS_COOKIE_NAME
): string {
  const next = withFolderGrant(cookieHeader, folderId, cookieName)
  return `${cookieName}=${encodeURIComponent(JSON.stringify(next))}; Max-Age=${
    60 * 60 * 24 * 30
  }; Path=/; SameSite=Lax`
}
